import { useEffect, useState } from 'react';
import { LayoutGrid, Table as TableIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ViewMode = 'cards' | 'table';

/** Persistent per-page view-mode preference. Each list page passes a unique
 *  `key` (e.g. 'tasks', 'creatives') so different pages remember independently. */
export function useViewMode(key: string, defaultMode: ViewMode = 'cards'): [ViewMode, (m: ViewMode) => void] {
  const storageKey = `kaf-view-${key}`;
  const [mode, setModeState] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return defaultMode;
    const v = window.localStorage.getItem(storageKey);
    return v === 'table' || v === 'cards' ? v : defaultMode;
  });
  useEffect(() => {
    try { window.localStorage.setItem(storageKey, mode); } catch { /* */ }
  }, [storageKey, mode]);
  return [mode, setModeState];
}

interface Props {
  mode: ViewMode;
  onChange: (m: ViewMode) => void;
  className?: string;
}

/** Two-button segmented control: Cards | Table. Drop in any list page header. */
export default function ViewToggle({ mode, onChange, className }: Props) {
  return (
    <div className={cn('inline-flex items-center rounded-lg border border-line bg-bg-card overflow-hidden', className)}>
      <button
        onClick={() => onChange('cards')}
        title="Card view"
        className={cn(
          'px-3 py-2 min-h-[36px] text-xs inline-flex items-center gap-1.5 transition',
          mode === 'cards'
            ? 'bg-accent-600/20 text-accent-400'
            : 'text-muted hover:text-muted-strong'
        )}
      >
        <LayoutGrid size={13} /> Cards
      </button>
      <button
        onClick={() => onChange('table')}
        title="Table view"
        className={cn(
          'px-3 py-2 min-h-[36px] text-xs inline-flex items-center gap-1.5 transition border-l border-line',
          mode === 'table'
            ? 'bg-accent-600/20 text-accent-400'
            : 'text-muted hover:text-muted-strong'
        )}
      >
        <TableIcon size={13} /> Table
      </button>
    </div>
  );
}
